import { EmptyState, FlexColumn, Loading, MaterialIcon, useLoading, useModal } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { CONFIGURATION_TRIGGER_TYPE_LABELS } from '../../../constants/maintenances';
import { TABLE_COLUMNS_NAME } from '../../../constants/maintenanceConfigurations';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { ConfigurationTriggerType, MaintenanceConfiguration } from '../../../types/maintenances';
import { StyledPaginatedTable } from '../../shared/StyledPaginatedTable';
import StyledTooltip from '../../ui/StyledTooltip';
import useMaintenanceConfigurations from '../hooks/useMaintenanceConfigurations';
import MaintenanceConfigurationModal from './MaintenanceConfigurationModal';

function MaintenanceConfigurationList() {
  const { theme, css, classes } = useStyles();
  const [maintenanceConfigurations, setMaintenanceConfigurations] = useState<MaintenanceConfiguration[]>();
  const {
    page,
    onReload,
    onChangePage,
    loadFleetMaintenanceConfigurations,
    reload,
    search,
    filters: { maintenanceTypeId, triggerType },
  } = useMaintenanceConfigurations();
  const [totalItems, setTotalItems] = useState(0);
  const { loading, stopLoading, startLoading } = useLoading(true);
  const { showFailFeedback } = useFeedback();
  const [selectedMaintenanceConfiguration, setSelectedMaintenanceConfiguration] = useState<MaintenanceConfiguration>();
  const { openModal, closeModal, showModal } = useModal();

  useUpdateEffect(() => {
    onChangePage(0);
  }, [search, reload, maintenanceTypeId, triggerType]);

  useEffect(() => {
    if (page === 0) {
      onChangePage(1);
      return;
    }
    const loadData = async () => {
      startLoading();
      await loadAssets();
      stopLoading();
    };

    loadData();
  }, [page]);

  const loadAssets = async () => {
    const { configurations, error, total } = await loadFleetMaintenanceConfigurations();
    if (error) {
      showFailFeedback(error);
      setMaintenanceConfigurations(undefined);
      setTotalItems(0);
    } else {
      setMaintenanceConfigurations(configurations);
      setTotalItems(total ?? 0);
    }
  };

  const onOpenDetailModal = (maintenanceConfiguration: MaintenanceConfiguration) => {
    setSelectedMaintenanceConfiguration(maintenanceConfiguration);
    openModal();
  };

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
      {!maintenanceConfigurations && !loading && <EmptyState title="Todavía no cuentas con ninguna configuración." />}
      {!maintenanceConfigurations && loading && <Loading />}
      {maintenanceConfigurations && (
        <>
          {!!maintenanceConfigurations?.length && (
            <StyledPaginatedTable
              columns={TABLE_COLUMNS_NAME}
              data={maintenanceConfigurations.map((maintenanceConfiguration) => {
                const pluralLabel = !(maintenanceConfiguration.trigger === 1);
                const triggerTypeLabel =
                  maintenanceConfiguration.triggerType === ConfigurationTriggerType.distance
                    ? 'km'
                    : pluralLabel
                    ? 'días'
                    : 'día';
                return {
                  items: [
                    maintenanceConfiguration.name,
                    maintenanceConfiguration.description,
                    CONFIGURATION_TRIGGER_TYPE_LABELS[maintenanceConfiguration.triggerType] ?? '-',
                    maintenanceConfiguration.trigger.toLocaleString() + ` ${triggerTypeLabel}`,
                    maintenanceConfiguration.maintenanceType.value,
                    <StyledTooltip
                      key={`details-${maintenanceConfiguration._id}`}
                      content="Editar"
                      showArrow={false}
                    >
                      <Button
                        size="mini"
                        shape="square"
                        onClick={() => onOpenDetailModal(maintenanceConfiguration)}
                        kind="tertiary"
                      >
                        <MaterialIcon
                          name="edit"
                          size={theme.sizing.scale700}
                        />
                      </Button>
                    </StyledTooltip>,
                  ],
                };
              })}
              totalItems={totalItems}
              page={page}
              onPageChange={onChangePage}
              loading={loading}
            />
          )}
          {!maintenanceConfigurations.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
        </>
      )}
      <MaintenanceConfigurationModal
        isOpen={showModal}
        onClose={(update) => {
          if (update) onReload();
          closeModal();
        }}
        selectedMaintenanceConfiguration={selectedMaintenanceConfiguration}
      />
    </FlexColumn>
  );
}

export default MaintenanceConfigurationList;
